import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMyPriorities } from "@/hooks/useMyPriorities";
import { priorityService } from "@/services/priorityService";
import {
  getApiErrorMessage,
  getAssignmentLimitMessage,
  getFocusUpdateRequiredMessage,
} from "@/lib/priority-errors";

interface PromoteQueueToFocusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The current user's pid — the row being promoted is always the caller's own. */
  userPid: string;
  /** Why the Focus slot is empty — only changes the copy, not the behaviour. */
  reason?: "stalled" | "done";
}

/**
 * Prompted right after the user's own Focus row leaves the Focus slot — either stalled into
 * Block Int/Ext or completed. With Focus now empty this offers a one-click promotion of a Queue item
 * into it, rather than picking a successor on the user's behalf: which item comes next is a planning
 * decision only they can make.
 */
export function PromoteQueueToFocusDialog({
  open,
  onOpenChange,
  userPid,
  reason = "stalled",
}: PromoteQueueToFocusDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: myPriorities, isLoading } = useMyPriorities(open ? userPid : undefined);
  // `p.status` is the priority's own rollup status, not this user's assignment status — on
  // multi-assignee priorities (e.g. DRI vs IC) those can differ, so filter on the caller's own
  // assignment row instead.
  const queueItems = (myPriorities?.data ?? []).filter(
    (p) => p.assignments?.find((a) => a.assignedUser.pid === userPid)?.status === "queue"
  );

  const promoteMutation = useMutation({
    mutationFn: (pid: string) => priorityService.updateOwnAssignment(pid, { status: "focus" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-onething"] });
      // The Priorities page list renders under this dialog too — without this
      // it keeps showing the promoted row as queue until a remount.
      queryClient.invalidateQueries({ queryKey: ["priorities"] });
      toast.success("Promoted to Focus");
      onOpenChange(false);
    },
    onError: (error) => {
      // The focus-limit and status-update rules name what actually blocks the promotion; the generic
      // API message would only echo a bare code, leaving a user at the limit with no next action.
      const message =
        getFocusUpdateRequiredMessage(error, "swap") ??
        getAssignmentLimitMessage(error) ??
        getApiErrorMessage(error);
      toast.error(message ?? "Failed to promote this item to Focus. Please try again.");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Promote a Queue item to Focus?
          </DialogTitle>
          <DialogDescription>
            {reason === "done"
              ? "Your Focus task is done. Pick a Queue item to pick up next, so you always have a Focus in progress."
              : "Your Focus task just moved to Block Int/Ext. Pick a Queue item to pick up next, so you always have a Focus in progress."}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : queueItems.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground">
            No Queue items to promote — add Queue items so a stalled Focus always has a successor.
          </div>
        ) : (
          <div className="flex max-h-[320px] flex-col gap-2 overflow-y-auto">
            {queueItems.map((item) => (
              <div
                key={item.pid}
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    <span className="mr-1.5 font-mono text-xs text-muted-foreground">{item.code}</span>
                    {item.name}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  disabled={promoteMutation.isPending}
                  onClick={() => promoteMutation.mutate(item.pid)}
                >
                  {promoteMutation.isPending && promoteMutation.variables === item.pid && (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  )}
                  Promote
                </Button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {queueItems.length === 0 && !isLoading ? (
            <Button
              type="button"
              onClick={() => {
                onOpenChange(false);
                navigate("/priorities");
              }}
            >
              Create a priority
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Dismiss
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
